import { useCallback, useEffect, useRef, useState } from 'react';

export function useDynamicTablePageSize({
  rowHeight = 40,
  fixedTop = 40,
  fixedBottom = 32,
  buffer = 11,
} = {}) {
  const [pageSize, setPageSize] = useState(100);
  const containerRef = useRef(null);
  const roRef = useRef(null);

  const computePageSize = useCallback(
    containerHeight => {
      if (!containerHeight || containerHeight === 0) return;
      const availableForRows = Math.floor(
        containerHeight - fixedTop - fixedBottom - buffer
      );
      const rowsFit = Math.floor(availableForRows / rowHeight);
      setPageSize(Math.max(rowsFit, 1));
    },
    [rowHeight, fixedTop, fixedBottom, buffer]
  );

  const setContainerRef = useCallback(
    el => {
      containerRef.current = el;

      if (!el) return;

      // Initial measure
      const containerHeight = el.getBoundingClientRect().height;
      computePageSize(containerHeight);

      // Observe container height
      const ro = new ResizeObserver(entries => {
        const cr = entries[0]?.contentRect;
        if (cr) {
          computePageSize(cr.height);
        }
      });
      ro.observe(el);
      roRef.current = ro;
    },
    [computePageSize]
  );

  // Cleanup on unmount
  useEffect(
    () => () => {
      if (roRef.current) {
        roRef.current.disconnect?.();
        roRef.current = null;
      }
      containerRef.current = null;
    },
    []
  );

  return { containerRef: setContainerRef, pageSize };
}
